<script lang="ts" setup>
import { computed } from 'vue'
import { QBtn } from 'quasar'
import { Color } from '@previo/codebooks'
import { PSpaceSize } from '../space/types'
import { PMaxLines } from '../typography/types'
import PSpace from '../space/PSpace.vue'
import PTextCta from '../typography/links/PTextCta.vue'
import PIconAlfredAi from '../icon/PIconAlfredAi.vue'
import { PBtnAiType } from './types'

type Props = {
  text?: null | string
  type?: PBtnAiType
  disable?: boolean
  loading?: boolean
  grow?: boolean
  cy: string
}

const props = withDefaults(defineProps<Props>(), {
  text: null,
  type: PBtnAiType.PRIMARY,
  disable: false,
  loading: false,
  grow: false,
})

type Emits = {
  (e: 'click', evt: Event): void
}

const emits = defineEmits<Emits>()

const isTertiary = computed<boolean>(() => {
  return props.type === PBtnAiType.TERTIARY
})

const textColor = computed<Color.WHITE | Color.AI | Color.GREY_500>(() => {
  if (props.disable) {
    return Color.GREY_500
  }

  return isTertiary.value ? Color.AI : Color.WHITE
})

const click = (e: Event): void => {
  emits('click', e)
}
</script>

<template>
  <q-btn
    :flat="isTertiary"
    :text-color="textColor"
    :disable="disable"
    :loading="loading"
    :data-cy="cy"
    :class="[{ 'ds-btn-ai--primary': !isTertiary }, { 'p-full-width': grow }]"
    class="ds-btn-ai"
    unelevated
    no-caps
    no-wrap
    @click="click"
  >
    <template v-if="text">
      <p-space :mobile="PSpaceSize.SUB_HEADLINE" />
      <p-text-cta :text="text" :color="textColor" :max-lines="PMaxLines.ONE" />
      <p-space :mobile="PSpaceSize.SUB_HEADLINE" />
    </template>
    <p-icon-alfred-ai />
  </q-btn>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/mixins';
@import '../../assets/scss/shared/brand.module';

.ds-btn-ai {
  @include mobile {
    padding: 10px;
  }

  @include laptopAndLarge {
    padding: 4px 6px 4px 6px;
  }

  &--primary {
    background: linear-gradient(135deg, getColor('ai') 0%, getColor('aiDark') 100%);

    &.disabled {
      background: getColor('grey200') !important;
    }
  }
}
</style>
